import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Session recording is opt-in for identified users only (see index.html).
  useEffect(() => {
    if (!user) return;
    window.__koltIdentified = true;
    try { window.posthog?.identify?.(user.user_id || user.email, { email: user.email, role: user.role }); } catch { /* noop */ }
    window.__koltStartRecording?.();
  }, [user]);

  // Presence heartbeat (owner's live view): on login, every 45s, and on route change.
  useEffect(() => {
    if (!user) return;
    let last = "";
    const ping = () => {
      const path = window.location.pathname;
      api.post("/presence/ping", { path }).catch(() => {});
      last = path;
    };
    ping();
    const iv = setInterval(ping, 45000);
    const onRoute = setInterval(() => { if (window.location.pathname !== last) ping(); }, 3000);
    const onHide = () => { if (document.visibilityState === "visible") ping(); };
    document.addEventListener("visibilitychange", onHide);
    return () => { clearInterval(iv); clearInterval(onRoute); document.removeEventListener("visibilitychange", onHide); };
  }, [user]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // /auth/me returns 401 when unauthenticated — that's expected; treat
      // any other failure as a logged-out state but surface it in dev.
      if (err?.response?.status !== 401) {
        console.warn("[auth] /me failed:", err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, skip /me — AuthCallback exchanges first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout is best-effort on the server; still clear local state.
      console.warn("[auth] logout request failed:", err);
    }
    setUser(null);
  }, []);

  // Memoise the context value so consumers don't re-render on every parent render.
  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
